// ตารางสอนครู (ฝ่ายการเรียนการสอน) — เลือกครูแล้วดูตารางของคนนั้น
//
// ตัวตารางกับ PDF ใช้ของเดียวกับหน้า "ตารางสอนของฉัน" ผ่าน ./timetableGrid
// ต่างกันแค่ t_id มาจาก dropdown แทนที่จะมาจาก JWT
import React, { useEffect, useState, useCallback } from "react";
import Header from "../layouts/Header";
import Footer from "../layouts/Footer";
import { Card, Row, Col, Button, Dropdown, Form, Badge } from "react-bootstrap";
import { Link } from "react-router-dom";
import { getToken } from "../services/authorize";
import axiosInstance from "../services/axiosInterceptor";
import { useLoading } from "../contexts/LoadingContext";
import { getCurrentSemester } from "../utils/teachingUtils";
import {
  TimetableGrid,
  buildTimetableGrid,
  hasTimetableData,
  exportTimetablePDF,
  printTimetable,
} from "./timetableGrid";

const API = process.env.REACT_APP_API;

// เรียงชื่อครูตามลำดับอักษรไทย
//
// ปล่อยให้ MySQL เรียงอย่างเดียวไม่พอ: t_name ในฐานข้อมูลมีช่องว่างนำหน้า/ต่อท้าย
// ปนอยู่ (" ซูไฮดา  รือสะ ") ซึ่ง ORDER BY จะดันขึ้นไปอยู่บนสุดก่อนตัว ก ทั้งหมด
// จึงต้อง trim ก่อนเทียบ และใช้ localeCompare ภาษาไทยแทน utf8mb3_general_ci
const byThaiName = (a, b) =>
  String(a.t_name || "").trim().localeCompare(String(b.t_name || "").trim(), "th");

export default function TeacherTimetable() {
  const currentSkin = localStorage.getItem("skin-mode") ? "dark" : "";
  const [skin, setSkin] = useState(currentSkin);

  const switchSkin = (s) => {
    const tables = document.getElementsByTagName("table");
    for (const table of tables) {
      const theads = table.querySelectorAll("thead");
      if (s === "dark") {
        for (const thead of theads) { thead.classList.remove("table-secondary"); thead.classList.add("table-primary"); }
        table.classList.add("table-dark");
      } else {
        table.classList.remove("table-dark");
        for (const thead of theads) { thead.classList.remove("table-primary"); thead.classList.add("table-secondary"); }
      }
    }
  };
  switchSkin(skin);
  useEffect(() => { switchSkin(skin); }, [skin]);

  const { setLoading } = useLoading();

  const [yearList, setYearList] = useState([]);
  const [yearId, setYearId] = useState(() => localStorage.getItem("yearId") || null);
  const [yearLabel, setYearLabel] = useState("");
  const [periods, setPeriods] = useState([]);
  const [teacherList, setTeacherList] = useState([]);
  const [teacherId, setTeacherId] = useState("");
  const [teachingData, setTeachingData] = useState([]);
  const [guidanceData, setGuidanceData] = useState([]);
  const [semester, setSemester] = useState(getCurrentSemester());

  const authHeader = useCallback(() => ({
    headers: { authorization: `Bearer ${getToken()}` },
  }), []);

  // ─── Load year list ───────────────────────────────────────────────────────
  useEffect(() => {
    axiosInstance.get(`${API}/getStudyYear`, authHeader()).then((res) => {
      const list = res.data || [];
      setYearList(list);
      let id = localStorage.getItem("yearId");
      if (!id && list.length > 0) { id = list[0].year_id; localStorage.setItem("yearId", id); }
      setYearId(id);
      const sel = list.find((y) => String(y.year_id) === String(id));
      setYearLabel(sel ? `ปีการศึกษา ${sel.year_year}` : "");
    }).catch(() => {});
  }, []);

  const handleSelectYear = (id) => {
    const sel = yearList.find((y) => y.year_id === id);
    if (!sel) return;
    setYearId(id);
    setYearLabel(`ปีการศึกษา ${sel.year_year}`);
    localStorage.setItem("yearId", id);
  };

  // ─── Load teacher list ────────────────────────────────────────────────────
  // side=academic เหมือนหน้าจัดการการสอน/มอบหมายครูสอนแทน — ครูภาคศาสนามีโมดูลของตัวเอง
  useEffect(() => {
    axiosInstance.get(`${API}/getTeacherList?side=academic`, authHeader())
      .then((res) => setTeacherList([...(res.data || [])].sort(byThaiName)))
      .catch(() => setTeacherList([]));
  }, []);

  // ─── Load periods ─────────────────────────────────────────────────────────
  useEffect(() => {
    if (!yearId) return;
    axiosInstance.get(`${API}/getPeriodList/${yearId}`, authHeader())
      .then((res) => setPeriods(res.data.map((p) => ({
        ...p,
        period_begin: p.period_begin?.slice(0, 5),
        period_end: p.period_end?.slice(0, 5),
      }))))
      .catch(() => setPeriods([]));
  }, [yearId]);

  // ─── Load the selected teacher's timetable ────────────────────────────────
  useEffect(() => {
    if (!yearId || !teacherId) {
      setTeachingData([]);
      setGuidanceData([]);
      return;
    }
    setLoading(true);
    Promise.all([
      axiosInstance.get(`${API}/getTeachingDataByTeacherId/${teacherId}/${yearId}`, authHeader())
        .then((res) => res.data || [])
        .catch((err) => {
          if (err.response?.status === 404) return []; // ครูที่ยังไม่มีตารางสอน
          throw err;
        }),
      axiosInstance.get(`${API}/getGuidanceByAdvisor/${teacherId}/${yearId}`, authHeader())
        .then((res) => res.data || [])
        .catch(() => []),
    ])
      .then(([teach, guidance]) => { setTeachingData(teach); setGuidanceData(guidance); })
      .catch(() => { setTeachingData([]); setGuidanceData([]); })
      .finally(() => setLoading(false));
  }, [yearId, teacherId]);

  const grid = React.useMemo(
    () => buildTimetableGrid(teachingData, guidanceData, semester),
    [teachingData, guidanceData, semester]
  );

  const hasData = hasTimetableData(teachingData, guidanceData, semester);

  // ชื่อครูเอาจากรายชื่อ ไม่ใช่จากแถวตารางสอน — ครูที่ยังไม่มีตารางจะไม่มีแถวให้อ่านชื่อเลย
  // trim ด้วยเพราะชื่อจะไปโผล่บนหัวตาราง PDF และในชื่อไฟล์ที่ดาวน์โหลด
  const teacherName =
    teacherList.find((t) => String(t.t_id) === String(teacherId))?.t_name?.trim() || "";

  // จำนวนคาบต่อสัปดาห์ของเทอมที่เลือก — ตัวเลขที่ใช้เทียบภาระสอนระหว่างครูได้ทันที
  const slotCount = React.useMemo(
    () => Object.values(grid).reduce((sum, day) => sum + Object.keys(day).length, 0),
    [grid]
  );

  return (
    <React.Fragment>
      <Header onSkin={setSkin} />
      <div className="main main-app p-3 p-lg-4">

        {/* Top bar */}
        <div className="d-sm-flex align-items-center justify-content-between mb-4">
          <div>
            <ol className="breadcrumb fs-sm mb-1">
              <li className="breadcrumb-item"><Link to="#">ข้อมูลการเรียนการสอน</Link></li>
              <li className="breadcrumb-item active">ตารางสอนครู</li>
            </ol>
            <h4 className="main-title mb-0">ตารางสอนครู</h4>
          </div>
          <div className="d-flex align-items-center gap-2 mt-3 mt-md-0">
            <Dropdown>
              <Dropdown.Toggle variant="primary" id="dropdown-year">
                {yearLabel || "เลือกปีการศึกษา"}
              </Dropdown.Toggle>
              <Dropdown.Menu>
                {yearList.map((y) => (
                  <Dropdown.Item key={y.year_id} onClick={() => handleSelectYear(y.year_id)}>
                    {`ปีการศึกษา ${y.year_year}`}
                  </Dropdown.Item>
                ))}
              </Dropdown.Menu>
            </Dropdown>
          </div>
        </div>

        <Row className="g-3">
          <Col xs={12}>
            <Card className="card-one">
              <Card.Header className="d-flex align-items-center justify-content-between flex-wrap gap-2">
                <div className="d-flex align-items-center gap-2 flex-wrap">
                  <Form.Select
                    value={teacherId}
                    onChange={(e) => setTeacherId(e.target.value)}
                    size="sm"
                    style={{ minWidth: 240 }}
                  >
                    <option value="">-- เลือกครู --</option>
                    {teacherList.map((t) => (
                      <option key={t.t_id} value={t.t_id}>{String(t.t_name || "").trim()}</option>
                    ))}
                  </Form.Select>
                  {teacherId && hasData && (
                    <Badge bg="light" text="dark">รวม {slotCount} คาบ/สัปดาห์</Badge>
                  )}
                </div>
                <div className="d-flex align-items-center gap-2">
                  <div className="btn-group" role="group">
                    <button
                      type="button"
                      className={`btn btn-sm ${semester === "1" ? "btn-primary" : "btn-outline-primary"}`}
                      onClick={() => setSemester("1")}
                    >
                      เทอม 1
                    </button>
                    <button
                      type="button"
                      className={`btn btn-sm ${semester === "2" ? "btn-primary" : "btn-outline-primary"}`}
                      onClick={() => setSemester("2")}
                    >
                      เทอม 2
                    </button>
                  </div>
                  <Button
                    variant="outline-danger"
                    size="sm"
                    disabled={periods.length === 0 || !hasData}
                    onClick={() => exportTimetablePDF({ periods, grid, teacherName, yearLabel, semester })}
                  >
                    <i className="ri-file-pdf-line me-1"></i>Export PDF
                  </Button>
                  <Button
                    variant="outline-secondary"
                    size="sm"
                    disabled={periods.length === 0 || !hasData}
                    onClick={() => printTimetable({ periods, grid, teacherName, yearLabel, semester })}
                  >
                    <i className="ri-printer-line me-1"></i>พิมพ์
                  </Button>
                </div>
              </Card.Header>

              <Card.Body className="p-2">
                {!teacherId ? (
                  <div className="text-center text-muted py-5">
                    <i className="ri-user-search-line fs-2 d-block mb-2"></i>
                    เลือกครูเพื่อดูตารางสอน
                  </div>
                ) : periods.length === 0 ? (
                  <div className="text-center text-muted py-5">ยังไม่ได้กำหนดคาบเรียนสำหรับปีนี้</div>
                ) : !hasData ? (
                  <div className="text-center text-muted py-5">
                    <i className="ri-calendar-close-line fs-2 d-block mb-2"></i>
                    {teacherName} ไม่มีตารางสอนในเทอมนี้
                  </div>
                ) : (
                  <TimetableGrid periods={periods} grid={grid} semester={semester} />
                )}
              </Card.Body>
            </Card>
          </Col>
        </Row>

        <Footer />
      </div>
    </React.Fragment>
  );
}
